// src/services/authService.js

import api from "./api";

/**
 * 🔐 Servicio de Autenticación
 * Maneja todas las operaciones relacionadas con login, registro y tokens
 */

const STORAGE_KEYS = {
  accessToken: "token",
  refreshToken: "refreshToken",
  user: "user",
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

export const socioLogin = (email, codigo_ingreso) =>
  api.post("/socio/login/", { email, codigo_ingreso });
export const crearCodigoIngreso = (email, codigo_ingreso) =>
  api.post("/socio/primer-acceso/", { email, codigo_ingreso });

const persistSession = (data = {}) => {
  const access = data.tokens?.access;
  const refresh = data.tokens?.refresh;
  const user = data.user;

  if (access) {
    localStorage.setItem(STORAGE_KEYS.accessToken, access);
  }

  if (refresh) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, refresh);
  }

  if (user) {
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  }
};

const clearSession = () => {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.user);
};

const authService = {
  persistSession,

  /**
   * Iniciar sesión
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise} - Promesa con los datos del usuario y tokens
   */
  login: async (email, password) => {
    try {
      const response = await api.post("/auth/login/", {
        email,
        password,
      });

      persistSession(response.data);

      return response.data;
    } catch (error) {
      // Manejo de errores más específico
      if (error.response) {
        // El servidor respondió con un código de error
        throw new Error(error.response.data.detail || "Credenciales inválidas");
      } else if (error.request) {
        // La petición se hizo pero no hubo respuesta
        throw new Error("No se pudo conectar con el servidor");
      } else {
        // Algo pasó al configurar la petición
        throw new Error("Error al procesar la solicitud");
      }
    }
  },

  /**
   * Registrar nuevo usuario
   * @param {Object} userData - Datos del usuario (email, password, username, etc.)
   * @returns {Promise} - Promesa con los datos del usuario registrado
   */
  register: async (userData) => {
    try {
      const response = await api.post("/auth/register/", userData);

      persistSession(response.data);

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.detail || "Error al registrar usuario"
        );
      }
      throw new Error("Error de conexión");
    }
  },

  /**
   * Obtener usuario actual (verificar token)
   * @returns {Promise} - Promesa con los datos del usuario actual
   */
  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.accessToken);

      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const response = await api.get("/auth/me/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Actualizamos los datos del usuario en localStorage
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(response.data));

      return response.data;
    } catch (error) {
      // Si el token es inválido, limpiamos todo
      authService.logout();
      throw new Error("Sesión inválida o expirada");
    }
  },

  /**
   * Cerrar sesión
   */
  logout: () => {
    clearSession();
  },

  /**
   * Verificar si el usuario está autenticado
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !!localStorage.getItem(STORAGE_KEYS.accessToken);
  },

  /**
   * Obtener el token actual
   * @returns {string|null}
   */
  getToken: () => {
    return localStorage.getItem(STORAGE_KEYS.accessToken);
  },

  /**
   * Obtener usuario desde localStorage
   * @returns {Object|null}
   */
  getStoredUser: () => {
    const user = localStorage.getItem(STORAGE_KEYS.user);
    return user ? JSON.parse(user) : null;
  },

  getStorageKeys: () => STORAGE_KEYS,

  getApiBaseUrl: () => API_BASE_URL,

  getAuthHeaders: () => {
    const token = localStorage.getItem(STORAGE_KEYS.accessToken);
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

export default authService;
